import React, {
  useState,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  ArrowRight,
  Table,
  Save,
  RefreshCw,
  Info,
  Download,
  FileText,
  ChevronDown,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import useAtelier2Store from '../../../stores/useAtelier2Store';
import { showSuccessToast, showErrorToast } from '../../../utils/toastUtils';
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';
import SourceRiskDreadedEventMapping from "./SourceRiskDreadedEventMapping";
import GuideModal from "./GuideModal";
import { createIdGenerator } from "../../../utils/idGenerator";
import dreadedEventService from "../../../services/dreadedEventsService";

const Activite3 = (props) => {
  const { t } = useTranslation();
  const analysisStore = useAnalysisStore();
  const atelier1Store = useAtelier1Store();
  const atelier2Store = useAtelier2Store();
  const currentAnalysis = analysisStore.currentAnalysis;
  const currentContextData = atelier1Store.contextData;
  const getSourcesDeRisque = atelier2Store.getSourcesDeRisque;
  const selectedCouplesSROV = useAtelier2Store((s) => s.selectedCouplesSROV);
  const currentDreadedEvents = atelier1Store.dreadedEvents;

  const [isSaving, setIsSaving] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
  const [isDocxMenuOpen, setIsDocxMenuOpen] = useState(false);

  const mappingRef = useRef(null);

  useSilentSaveListener('atelier2-activite3', () => {
    if (mappingRef.current) {
      return mappingRef.current.silentSave();
    }
    return 'skipped';
  });

  // Function to pass to child component
  const updateIsSaving = (value) => {
    setIsSaving(value);
  };

  // Helper to reliably assure dreaded events are loaded for export handlers
  const getEnsureDreadedEvents = async () => {
    if (currentDreadedEvents && currentDreadedEvents.length > 0) {
      return currentDreadedEvents;
    }
    if (currentAnalysis?.id) {
      try {
        const response = await dreadedEventService.getDreadedEvents(currentAnalysis.id);
        if (response.success && response.data) {
          return Array.isArray(response.data) ? response.data :
                 (response.data.data?.dreadedEvents || response.data.dreadedEvents || []);
        }
      } catch (error) {
        console.error("Error fetching dreaded events for export:", error);
      }
    }
    return [];
  };

  // PDF Export Handler
  const handleExportPDF = async () => {
    try {
      // Load data
      const sourcesData = await getSourcesDeRisque(currentAnalysis?.id);
      const mappingsData = selectedCouplesSROV;
      const dreadedEvents = await getEnsureDreadedEvents();

      // Filter only retained sources (what's visible on page)
      const retainedSources = sourcesData?.filter((s) => s.selected) || [];
      const retainedSourceIds = new Set(retainedSources.map((s) => s.id));

      // Filter mappings to only include those with retained sources
      const visibleMappings = (mappingsData || []).filter((mapping) =>
        retainedSourceIds.has(mapping.sourceId),
      );

      // Prepare mappings with full details
      const detailedMappings = visibleMappings.map((mapping) => {
        const source = retainedSources.find((s) => s.id === mapping.sourceId);
        const event = dreadedEvents.find(
          (e) => e.id === mapping.dreadedEventId,
        );

        return {
          sourceName: source?.name || "N/A",
          sourceObjective: source?.objectifVise || "N/A",
          sourceMotivation: source?.motivation || "N/A",
          sourceActivity: source?.activite || "N/A",
          sourceResources: source?.ressources || "N/A",
          eventName: event?.name || "N/A",
          eventPillar: event?.securityPillar || "N/A",
          eventSeverity: event?.severity || "N/A",
          eventImpacts: event?.impacts
            ? event.impacts
                .map((i) => (typeof i === "string" ? i : i.impact || String(i)))
                .join(", ")
            : "N/A",
        };
      });

      const reportData = {
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalSources: retainedSources.length,
        totalEvents: dreadedEvents.length,
        totalMappings: visibleMappings.length, // Only visible mappings
        mappings: detailedMappings,
      };

      // Import PDF generator dynamically
      const { default: SRERMappingsPdfGenerator } =
        await import("../../../utils/srerMappingsPdfGenerator.js");

      // Create PDF generator instance
      const pdfGenerator = new SRERMappingsPdfGenerator(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      });

      // Generate PDF
      const pdfBlob = await pdfGenerator.generateSRERMappingsReport();

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EBIOS-RM_Associations-SRER_${currentContextData?.organizationName || "Organisation"}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      showSuccessToast("Rapport PDF généré avec succès");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      showErrorToast(`Erreur lors de l'export PDF: ${error.message}`);
    }
  };

  // PDF Export Handler (English)
  const handleExportPDFEn = async () => {
    try {
      // Load data (same as French)
      const sourcesData = await getSourcesDeRisque(currentAnalysis?.id);
      const mappingsData = selectedCouplesSROV;
      const dreadedEvents = await getEnsureDreadedEvents();

      // Filter only retained sources (what's visible on page)
      const retainedSources = sourcesData?.filter((s) => s.selected) || [];
      const retainedSourceIds = new Set(retainedSources.map((s) => s.id));

      // Filter mappings to only include those with retained sources
      const visibleMappings = (mappingsData || []).filter((mapping) =>
        retainedSourceIds.has(mapping.sourceId),
      );

      // Prepare mappings with full details
      const detailedMappings = visibleMappings.map((mapping) => {
        const source = retainedSources.find((s) => s.id === mapping.sourceId);
        const event = dreadedEvents.find(
          (e) => e.id === mapping.dreadedEventId,
        );

        return {
          sourceName: source?.name || "N/A",
          sourceObjective: source?.objectifVise || "N/A",
          sourceMotivation: source?.motivation || "N/A",
          sourceActivity: source?.activite || "N/A",
          sourceResources: source?.ressources || "N/A",
          eventName: event?.name || "N/A",
          eventPillar: event?.securityPillar || "N/A",
          eventSeverity: event?.severity || "N/A",
          eventImpacts: event?.impacts
            ? event.impacts
                .map((i) => (typeof i === "string" ? i : i.impact || String(i)))
                .join(", ")
            : "N/A",
        };
      });

      const reportData = {
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalSources: retainedSources.length,
        totalEvents: dreadedEvents.length,
        totalMappings: visibleMappings.length, // Only visible mappings
        mappings: detailedMappings,
      };

      // Import English PDF generator dynamically
      const { default: SRERMappingsPdfGenerator } =
        await import("../../../utils/srerMappingsPdfGenerator.js");

      // Create PDF generator instance
      const pdfGenerator = new SRERMappingsPdfGenerator(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      }, "en");

      // Generate PDF
      const pdfBlob = await pdfGenerator.generateSRERMappingsReport();

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EBIOS-RM_RS-DE-Mappings_${currentContextData?.organizationName || "Organization"}_${new Date().toISOString().slice(0, 10)}_EN.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      showSuccessToast("PDF Report (EN) generated successfully");
    } catch (error) {
      console.error("Error exporting PDF (EN):", error);
      showErrorToast(`Error exporting PDF (EN): ${error.message}`);
    }
  };

  // DOCX Export Handler
  const handleExportDocx = async () => {
    try {
      const sourcesData = await getSourcesDeRisque(currentAnalysis?.id);
      const mappingsData = selectedCouplesSROV;
      const dreadedEvents = await getEnsureDreadedEvents();

      const retainedSources = sourcesData?.filter((s) => s.selected) || [];
      const retainedSourceIds = new Set(retainedSources.map((s) => s.id));

      const visibleMappings = (mappingsData || []).filter((mapping) =>
        retainedSourceIds.has(mapping.sourceId),
      );

      const detailedMappings = visibleMappings.map((mapping) => {
        const source = retainedSources.find((s) => s.id === mapping.sourceId);
        const event = dreadedEvents.find(
          (e) => e.id === mapping.dreadedEventId,
        );

        return {
          sourceName: source?.name || "N/A",
          sourceObjective: source?.objectifVise || "N/A",
          sourceMotivation: source?.motivation || "N/A",
          sourceActivity: source?.activite || "N/A",
          sourceResources: source?.ressources || "N/A",
          eventName: event?.name || "N/A",
          eventPillar: event?.securityPillar || "N/A",
          eventSeverity: event?.severity || "N/A",
          eventImpacts: event?.impacts
            ? event.impacts
                .map((i) => (typeof i === "string" ? i : i.impact || String(i)))
                .join(", ")
            : "N/A",
        };
      });

      const reportData = {
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalSources: retainedSources.length,
        totalEvents: dreadedEvents.length,
        totalMappings: visibleMappings.length,
        mappings: detailedMappings,
      };

      const { generateSRERMappingsDocx } =
        await import("../../../utils/srerMappingsDocxGenerator.js");

      await generateSRERMappingsDocx(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      });

      showSuccessToast("Rapport Word généré avec succès");
    } catch (error) {
      console.error("Error exporting DOCX:", error);
      showErrorToast(`Erreur lors de l'export Word: ${error.message}`);
    }
  };

  // DOCX Export Handler (English)
  const handleExportDocxEn = async () => {
    try {
      const sourcesData = await getSourcesDeRisque(currentAnalysis?.id);
      const mappingsData = selectedCouplesSROV;
      const dreadedEvents = await getEnsureDreadedEvents();

      const retainedSources = sourcesData?.filter((s) => s.selected) || [];
      const retainedSourceIds = new Set(retainedSources.map((s) => s.id));

      const visibleMappings = (mappingsData || []).filter((mapping) =>
        retainedSourceIds.has(mapping.sourceId),
      );

      const detailedMappings = visibleMappings.map((mapping) => {
        const source = retainedSources.find((s) => s.id === mapping.sourceId);
        const event = dreadedEvents.find(
          (e) => e.id === mapping.dreadedEventId,
        );

        return {
          sourceName: source?.name || "N/A",
          sourceObjective: source?.objectifVise || "N/A",
          sourceMotivation: source?.motivation || "N/A",
          sourceActivity: source?.activite || "N/A",
          sourceResources: source?.ressources || "N/A",
          eventName: event?.name || "N/A",
          eventPillar: event?.securityPillar || "N/A",
          eventSeverity: event?.severity || "N/A",
          eventImpacts: event?.impacts
            ? event.impacts
                .map((i) => (typeof i === "string" ? i : i.impact || String(i)))
                .join(", ")
            : "N/A",
        };
      });

      const reportData = {
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalSources: retainedSources.length,
        totalEvents: dreadedEvents.length,
        totalMappings: visibleMappings.length,
        mappings: detailedMappings,
      };

      const { generateSRERMappingsDocx } =
        await import("../../../utils/srerMappingsDocxGenerator.js");

      await generateSRERMappingsDocx(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      }, "en");

      showSuccessToast("Word Report (EN) generated successfully");
    } catch (error) {
      console.error("Error exporting DOCX (EN):", error);
      showErrorToast(`Error exporting Word (EN): ${error.message}`);
    }
  };

  // CSV Export Handler (French)
  const handleExportCSV = async () => {
    try {
      const sourcesData = await getSourcesDeRisque(currentAnalysis?.id);
      const mappingsData = selectedCouplesSROV;
      const dreadedEvents = await getEnsureDreadedEvents();

      const mappings = [];

      mappingsData.forEach((mapping) => {
        if (!mapping) return;
        const source = sourcesData?.find(
          (s) => String(s.id) === String(mapping.sourceId),
        );
        const event = dreadedEvents.find(
          (e) => String(e.id) === String(mapping.dreadedEventId),
        );

        if (!source || !event) return;

        mappings.push({
          sourceName: source.name || "",
          objectifVise: source.objectifVise || source.objectifViseCategory || "",
          dreadedEvent: event.name || "",
          selected: source.selected || false,
          justification: source.justification || "",
        });
      });

      const reportData = {
        mappings: mappings,
      };

      const { default: SRERMappingsCsvGenerator } =
        await import("../../../utils/srerMappingsCsvGenerator.js");

      const csvGenerator = new SRERMappingsCsvGenerator(reportData, "fr");
      csvGenerator.download();

      showSuccessToast("Rapport CSV généré avec succès");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      showErrorToast(`Erreur lors de l'export CSV: ${error.message}`);
    }
  };

  // CSV Export Handler (English)
  const handleExportCSVEn = async () => {
    try {
      const sourcesData = await getSourcesDeRisque(currentAnalysis?.id);
      const mappingsData = selectedCouplesSROV;
      const dreadedEvents = await getEnsureDreadedEvents();

      const mappings = [];

      mappingsData.forEach((mapping) => {
        if (!mapping) return;
        const source = sourcesData?.find(
          (s) => String(s.id) === String(mapping.sourceId),
        );
        const event = dreadedEvents.find(
          (e) => String(e.id) === String(mapping.dreadedEventId),
        );

        if (!source || !event) return;

        mappings.push({
          sourceName: source.name || "",
          objectifVise: source.objectifVise || source.objectifViseCategory || "",
          dreadedEvent: event.name || "",
          selected: source.selected || false,
          justification: source.justification || "",
        });
      });

      const reportData = {
        mappings: mappings,
      };

      const { default: SRERMappingsCsvGenerator } =
        await import("../../../utils/srerMappingsCsvGenerator.js");

      const csvGenerator = new SRERMappingsCsvGenerator(reportData, "en");
      csvGenerator.download();

      showSuccessToast("CSV Report (EN) generated successfully");
    } catch (error) {
      console.error("Error exporting CSV (EN):", error);
      showErrorToast(`Error exporting CSV (EN): ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Modern Header with breadcrumb */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-6 rounded-xl shadow-md border border-gray-100"
      >
        <div className="flex items-center text-sm text-gray-500 mb-2">
          <span>{t("workshop2.title")}</span>
          <ArrowRight size={14} className="mx-2" />
          <span className="font-medium text-blue-600">
            {t("workshop2.activity3.breadcrumb")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-blue-100 p-2 rounded-lg mr-4">
              <Shield size={24} className="text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {t("workshop2.activity3.title")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("workshop2.activity3.subtitle")}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Save button - will be controlled by child component */}
            <button
              id="save-mappings-button"
              disabled={isSaving}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-70 transition-all duration-200"
            >
              {isSaving ? (
                <RefreshCw size={18} className="mr-2 animate-spin" />
              ) : (
                <Save size={18} className="mr-2" />
              )}
              <span className="font-medium">{t("common.save")}</span>
            </button>

            {/* Consolidated Export Dropdown */}
            <div className="relative">
              <button
                className="text-sm font-medium bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center shadow-sm transition duration-200"
                onClick={() => setIsPdfMenuOpen(!isPdfMenuOpen)}
              >
                <FileText size={16} className="mr-2" />
                {t("common.export", "Exporter")}
                <ChevronDown
                  size={14}
                  className={`ml-1 transition-transform duration-200 ${isPdfMenuOpen ? "rotate-180" : ""}`}
                />
              </button>
              <AnimatePresence>
                {isPdfMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden z-20 origin-top-right"
                  >
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b border-slate-100 uppercase tracking-wider">
                      PDF Export
                    </div>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 flex items-center"
                      onClick={() => {
                        handleExportPDF();
                        setIsPdfMenuOpen(false);
                      }}
                    >
                      <span className="mr-2">🇫🇷</span> Français (PDF)
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 flex items-center"
                      onClick={() => {
                        handleExportPDFEn();
                        setIsPdfMenuOpen(false);
                      }}
                    >
                      <span className="mr-2">🇬🇧</span> English (PDF)
                    </button>

                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b border-slate-100 uppercase tracking-wider">
                      Word Export
                    </div>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 flex items-center"
                      onClick={() => {
                        handleExportDocx();
                        setIsPdfMenuOpen(false);
                      }}
                    >
                      <span className="mr-2">🇫🇷</span> Français (Word)
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center"
                      onClick={() => {
                        handleExportDocxEn();
                        setIsPdfMenuOpen(false);
                      }}
                    >
                      <span className="mr-2">🇬🇧</span> English (Word)
                    </button>

                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b border-slate-100 uppercase tracking-wider">
                      CSV Export
                    </div>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 flex items-center"
                      onClick={() => {
                        handleExportCSV();
                        setIsPdfMenuOpen(false);
                      }}
                    >
                      <span className="mr-2">🇫🇷</span> Français (CSV)
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center"
                      onClick={() => {
                        handleExportCSVEn();
                        setIsPdfMenuOpen(false);
                      }}
                    >
                      <span className="mr-2">🇬🇧</span> English (CSV)
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Help Button */}
            <button
              className="text-sm font-medium bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center shadow-sm transition duration-200"
              onClick={() => setIsGuideOpen(true)}
            >
              <Info size={16} className="mr-2" />
              {t("common.help")}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Guide Modal - positioned right under the header */}
      <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      {/* Content */}
      <div className="bg-transparent overflow-hidden">
        <SourceRiskDreadedEventMapping
          ref={mappingRef}
          showSummaryTable={true}
          setShowSummaryTable={() => {}}
          isSaving={isSaving}
          setIsSaving={updateIsSaving}
          saveButtonId="save-mappings-button"
        />
      </div>
    </div>
  );
};

export default Activite3;
